import { useMemo, useState, Fragment } from "react";
import { Trash2, Search, X, ChevronDown } from "lucide-react";
import { fmtDate } from "@/lib/api";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

const SORTS = [
  { key: "created_desc", label: "Newest signup" },
  { key: "created_asc", label: "Oldest signup" },
  { key: "login_desc", label: "Recently active" },
  { key: "alerts_desc", label: "Most alerts sent" },
  { key: "segments_desc", label: "Highest SMS cost" },
];

const short = (v) => (v ? fmtDate(v) : "-");
const Yes = ({ on }) => (
  <span className={on ? "font-semibold text-emerald-600" : "text-slate-300"}>{on ? "Yes" : "No"}</span>
);

const sortUsers = (rows, key) => {
  const by = (f) => (a, b) => String(b[f] || "").localeCompare(String(a[f] || ""));
  const num = (f) => (a, b) => (b[f] || 0) - (a[f] || 0);
  if (key === "created_asc") return [...rows].sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  if (key === "login_desc") return [...rows].sort(by("last_login_at"));
  if (key === "alerts_desc") return [...rows].sort(num("alerts_sent"));
  if (key === "segments_desc") return [...rows].sort(num("segments_sent"));
  return [...rows].sort(by("created_at"));
};

const select = "rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900";

export const AdminUsers = ({ users, busy, onDelete }) => {
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("all");
  const [state, setState] = useState("all");
  const [flag, setFlag] = useState("all");
  const [sort, setSort] = useState("created_desc");
  const [open, setOpen] = useState(null);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = users.filter((u) => {
      if (needle && ![u.mobile_number, u.email, u.state, ...(u.allergies || []), u.subscription_plan]
        .filter(Boolean).join(" ").toLowerCase().includes(needle)) return false;
      if (plan === "paid" && !u.subscription_active) return false;
      if (plan === "free" && u.subscription_active) return false;
      if (state !== "all" && u.state !== state) return false;
      if (flag === "welcomed" && !u.welcome_sent_at) return false;
      if (flag === "not_welcomed" && u.welcome_sent_at) return false;
      if (flag === "alerted" && !u.first_alert_at) return false;
      if (flag === "sms_off" && u.sms_enabled) return false;
      if (flag === "closed" && !u.deleted_at) return false;
      return true;
    });
    return sortUsers(out, sort);
  }, [users, q, plan, state, flag, sort]);

  const filtersOn = q.trim() || plan !== "all" || state !== "all" || flag !== "all";
  const paying = users.filter((u) => u.subscription_active).length;

  return (
    <div className="space-y-3" data-testid="admin-users">
      <div className="space-y-3 rounded-md border border-slate-200 bg-white p-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="user-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search number, email, state, allergy or plan"
            className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
          {q && (
            <button data-testid="user-search-clear" onClick={() => setQ("")} aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="user-plan-filter" value={plan} onChange={(e) => setPlan(e.target.value)} className={select}>
            <option value="all">All plans</option>
            <option value="paid">Paying only</option>
            <option value="free">Free only</option>
          </select>
          <select data-testid="user-state-filter" value={state} onChange={(e) => setState(e.target.value)} className={select}>
            <option value="all">All states</option>
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="user-flag-filter" value={flag} onChange={(e) => setFlag(e.target.value)} className={select}>
            <option value="all">Any status</option>
            <option value="welcomed">Welcome SMS sent</option>
            <option value="not_welcomed">Welcome SMS not sent</option>
            <option value="alerted">Has had a real alert</option>
            <option value="sms_off">SMS switched off</option>
            <option value="closed">Account closed</option>
          </select>
          <select data-testid="user-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={select}>
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {filtersOn && (
            <button data-testid="user-clear-filters"
              onClick={() => { setQ(""); setPlan("all"); setState("all"); setFlag("all"); }}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100">
              <X className="h-3 w-3" /> Clear
            </button>
          )}
          <span className="ml-auto text-xs text-slate-500" data-testid="users-summary">
            {rows.length} of {users.length} accounts / {paying} paying
          </span>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-md border border-slate-200 bg-white p-6 text-sm text-slate-600" data-testid="users-empty">
          No users match these filters.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {["Mobile", "Email", "Plan", "State", "Signed up", "Last login", "Welcome", "Example", "Alerts", "Cost", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <Fragment key={u.id}>
                  <tr data-testid={`user-row-${u.id}`}
                    className={`border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${u.deleted_at ? "opacity-50" : ""}`}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-slate-900" data-testid={`user-mobile-${u.id}`}>
                      {u.mobile_number}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700" data-testid={`user-email-${u.id}`}>
                      {u.email || <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-3 py-2" data-testid={`user-plan-${u.id}`}>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        u.deleted_at ? "bg-slate-100 text-slate-500"
                          : u.subscription_active ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {u.deleted_at ? "Closed" : u.subscription_active ? "Paid" : "Free"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{u.state || "-"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{short(u.created_at)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600" data-testid={`user-last-login-${u.id}`}>{short(u.last_login_at)}</td>
                    <td className="px-3 py-2 text-xs" data-testid={`user-welcome-${u.id}`}><Yes on={!!u.welcome_sent_at} /></td>
                    <td className="px-3 py-2 text-xs" data-testid={`user-example-${u.id}`}><Yes on={!!u.example_status} /></td>
                    <td className="px-3 py-2 text-xs text-slate-700" data-testid={`user-alerts-${u.id}`}>{u.alerts_sent}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{u.segments_sent} seg</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`user-expand-${u.id}`} onClick={() => setOpen(open === u.id ? null : u.id)}
                          aria-label="Details" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                          <ChevronDown className={`h-4 w-4 transition-transform ${open === u.id ? "rotate-180" : ""}`} />
                        </button>
                        <button data-testid={`user-delete-${u.id}`} disabled={busy} onClick={() => onDelete(u)}
                          aria-label="Delete number"
                          className="rounded p-1 text-red-600 hover:bg-red-50 disabled:opacity-40">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {open === u.id && (
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <td colSpan={11} className="px-3 py-3" data-testid={`user-detail-${u.id}`}>
                        <div className="grid gap-x-6 gap-y-2 text-xs sm:grid-cols-3 lg:grid-cols-4">
                          <p><span className="label-caps">Coverage</span><br />{u.coverage === "all" ? "All of Australia" : "State + national"}</p>
                          <p><span className="label-caps">Allergies</span><br />{u.allergies?.length ? u.allergies.join(", ") : "none"}</p>
                          <p><span className="label-caps">Alert types</span><br />{(u.alert_types || ["all"]).join(", ")}</p>
                          <p><span className="label-caps">Subscription</span><br />{u.subscription_plan || "-"} ({u.subscription_status})</p>
                          <p><span className="label-caps">Welcome sent</span><br />{short(u.welcome_sent_at)} {u.welcome_status ? `(${u.welcome_status})` : ""}</p>
                          <p><span className="label-caps">First alert</span><br />{short(u.first_alert_at)}</p>
                          <p><span className="label-caps">Last alert</span><br />{short(u.last_alert_at)}</p>
                          <p><span className="label-caps">Flags</span><br />
                            Verified <Yes on={u.mobile_verified} /> / Onboarded <Yes on={u.onboarded} /> / SMS <Yes on={u.sms_enabled} />
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-500">
        Deleting removes the number from the database permanently and scrubs it from the SMS log.
      </p>
    </div>
  );
};
